import { formatError, type FormattedError } from '@shared/utils/RequestUtils';
import { useAuthStore } from '@stores/authStore';
import axios, { AxiosError, AxiosInstance, AxiosResponse, CanceledError, type InternalAxiosRequestConfig } from 'axios';

interface ApiClientOptions {
  baseURL: string;
  headers?: () => Record<string, string>;
  getToken: () => string | null;
  withCredentials?: boolean;
}

const isSentWithCurrentToken = (error: AxiosError, currentToken: string | null): boolean =>
  currentToken !== null && error.config?.headers?.Authorization === `Bearer ${currentToken}`;

const createApiClient = ({ baseURL, headers = () => ({}), getToken }: ApiClientOptions): AxiosInstance => {
  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      ...headers(),
    },
  });
  const contexts = new WeakMap<InternalAxiosRequestConfig, { token: string | null; scope: string }>();
  let lastForbiddenRefresh: { token: string | null; scope: string } | null = null;
  useAuthStore.subscribe((state, previous) => {
    if (state.token !== previous.token) lastForbiddenRefresh = null;
  });
  const isCurrent = (config?: InternalAxiosRequestConfig) => {
    const context = config && contexts.get(config);
    return (
      context === undefined ||
      (context.token === getToken() && context.scope === useAuthStore.getState().getAuthorizationScope())
    );
  };

  client.interceptors.request.use((config) => {
    const token = getToken();
    contexts.set(config, { token, scope: useAuthStore.getState().getAuthorizationScope() });

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  });

  client.interceptors.response.use(
    (response: AxiosResponse) => {
      if (!isCurrent(response.config)) throw new CanceledError();
      return response;
    },
    (error: AxiosError): Promise<FormattedError> => {
      if (!isCurrent(error.config)) return Promise.reject(new CanceledError());
      // Solo un 401 de la sesión vigente la cierra: una petición enviada con un
      // token anterior no debe cerrar la sesión que se abrió después.
      if (error.response?.status === 401 && isSentWithCurrentToken(error, getToken())) {
        useAuthStore.getState().logout();
      }
      if (error.response?.status === 403) {
        if (error.config?.url === '/me') useAuthStore.getState().setAuthorization(null);
        else {
          const scope = useAuthStore.getState().getAuthorizationScope();
          const token = getToken();
          // One denied operation must not repeatedly refresh the same authority.
          if (!lastForbiddenRefresh || lastForbiddenRefresh.token !== token || lastForbiddenRefresh.scope !== scope) {
            lastForbiddenRefresh = { token, scope };
            useAuthStore.getState().invalidateAuthorization();
          }
        }
      }
      const formattedError = formatError(error);
      return Promise.reject(formattedError);
    },
  );

  return client;
};

export const apiClientWH = createApiClient({
  baseURL: import.meta.env.VITE_API_WH,
  getToken: () => useAuthStore.getState().token,
});
